import React from "react";
import { Search, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { SunbedSessionApiItem } from "../../services/sunbeds";
import { useSunbedTimer } from "../../hooks/useSunbedTimer";

interface SunbedSessionsTableProps {
  sessions: SunbedSessionApiItem[];
  loading: boolean;
  indexOfFirstItem: number;
  onFinish?: (id: string | number) => void;
  onCancel?: (id: string | number) => void;
}

const SessionRow: React.FC<{
  session: SunbedSessionApiItem;
  index: number;
  onFinish?: (id: string | number) => void;
  onCancel?: (id: string | number) => void;
}> = ({ session, index, onFinish, onCancel }) => {
  // Initial values from API
  const initialRemaining = session.mins_left.remaining;
  const initialDelay = session.mins_left.delay;
  const isSessionActive = session.status.label.toLowerCase() === 'running' || session.status.label.toLowerCase() === 'in-session';

  const { displayTime, phase, isCompleted } = useSunbedTimer({
    initialRemaining,
    initialDelay,
    isActive: isSessionActive,
    onComplete: () => {
      if (onFinish) {
        onFinish(session.id);
      }
    }
  });

  // Badge Logic
  let badgeClass = session.status.badge_class;
  let statusLabel = session.status.label;

  if (isSessionActive) {
    if (phase === 'delay') {
      statusLabel = "Delay";
      badgeClass = "bg-[#FFF3E0] text-[#E65100] border-[#FFCC80]"; // Customize delay color
    } else if (phase === 'active') {
      // Keep original active/running style
    } else if (phase === 'completed') {
      statusLabel = "Completed";
      badgeClass = "bg-[#E8F5E9] text-[#2E7D32] border-[#A5D6A7]";
    }
  }

  return (
    <tr className="border-b border-gray-100 hover:bg-purple-50/30 transition-colors bg-white group text-sm">
      <td className="px-6 py-4 text-gray-500 font-medium">
        {index + 1}
      </td>
      <td className="px-6 py-4 font-semibold text-gray-800 group-hover:text-[#885ABB] transition-colors">
        {session.sunbed_room}
      </td>
      <td className="px-6 py-4 text-gray-600">
        {session.customer}
      </td>
      <td className="px-6 py-4 text-gray-600 font-medium">
        {session.total_duration}
      </td>
      <td className="px-6 py-4 font-mono text-gray-600 font-bold">
        {phase === 'delay' && <span className="text-xs text-orange-600 mr-1">Delay:</span>}
        {displayTime}
      </td>
      <td className="px-6 py-4 text-center">
        <span
          className={cn(
            "inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-semibold border",
            badgeClass.includes('badge-success') ? "bg-[#E8F5E9] text-[#2E7D32] border-[#A5D6A7]" :
              badgeClass.includes('badge-info') ? "bg-[#E3F2FD] text-[#1565C0] border-[#90CAF9]" :
                badgeClass.includes('badge-danger') ? "bg-[#FFEBEE] text-[#C62828] border-[#FFCDD2]" :
                  badgeClass
          )}
        >
          {statusLabel}
        </span>
      </td>
      <td className="px-6 py-4 text-right">
        <div className="flex justify-end gap-2">
          {(isSessionActive && !isCompleted) && (
            <>
              {session.action?.type === 'cancel' && onCancel && (
                <button
                  onClick={() => onCancel(session.id)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded-full transition-colors"
                  title="Cancel Session"
                >
                  <X size={18} />
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

export const SunbedSessionsTable: React.FC<SunbedSessionsTableProps> = ({
  sessions,
  loading,
  indexOfFirstItem,
  onFinish,
  onCancel,
}) => {
  return (
    <div className="flex-1 overflow-y-auto relative">
      <table className="w-full">
        <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100">
          <tr className="text-sm font-bold text-white tracking-wide">
            <th className="px-6 py-4 text-left font-bold w-12">#</th>
            <th className="px-6 py-4 text-left font-bold">Sunbed Room</th>
            <th className="px-6 py-4 text-left font-bold">Customer</th>
            <th className="px-6 py-4 text-left font-bold">Total Duration</th>
            <th className="px-6 py-4 text-left font-bold">Minutes Left</th>
            <th className="px-6 py-4 text-center font-bold">Status</th>
            <th className="px-6 py-4 text-right font-bold w-24">Action</th>
          </tr>
        </thead>

        <tbody className="w-full divide-y divide-gray-100 bg-white">
          {loading ? (
            <tr className="h-64 w-full">
              <td colSpan={12} className="h-full">
                <div className="flex h-full w-full items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
                </div>
              </td>
            </tr>
          ) : sessions.length === 0 ? (
            <tr className="flex flex-col items-center justify-center h-64 text-gray-400 w-full">
              <td className="w-full h-full flex flex-col items-center justify-center">
                <Search className="w-12 h-12 mb-3 opacity-20" />
                <p className="text-lg font-medium text-gray-500">
                  No sessions found
                </p>
                <p className="text-sm">Try adjusting your search terms</p>
              </td>
            </tr>
          ) : (
            sessions.map((session, idx) => (
              <SessionRow
                key={session.id}
                session={session}
                index={indexOfFirstItem + idx}
                onFinish={onFinish}
                onCancel={onCancel}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
